import React, { useState, useRef, useEffect, MouseEventHandler } from "react";
import { Link } from "gatsby";
import { useGlobalState } from './global-context';
import { useLocation } from '@reach/router';
import { cn, getUrl, getCleanPathname } from "../lib/helpers";
import LinkElem from "./link";
import ImageG from "./image-g";
import Icon from "./icon";

import * as styles from "../styles/components/menu-item.module.css";

interface ImageNode {
  asset?: {
    _id?: string;
    url?: string;
  };
  alt?: string;
}

interface LinkNode {
  _key?: string;
  url?: string;
  blank?: boolean;
  text?: string;
}

interface SubmenuLink extends LinkNode {
  image?: ImageNode;
}

interface SubmenuBlock {
  title?: string;
  menu?: SubmenuLink[];
}

interface Submenu {
  primary?: SubmenuBlock;
  link?: LinkNode;
  secondary?: SubmenuBlock;
}

interface MenuItemLinkNode extends LinkNode {
  submenu?: Submenu;
  defaultImage?: ImageNode;
}

interface MenuItemProps {
  node?: MenuItemLinkNode;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  showMenu?: boolean;
  pageLoaded?: boolean;
}

const MenuItem: React.FC<MenuItemProps> = ({ 
  node, 
  onClick, 
  showMenu,
  pageLoaded
}) => {
  // Destructure node for cleanliness
  const { url, blank, text, submenu, defaultImage } = node || {};
  const { primary, link: additionalLink, secondary } = submenu || {};
  const { title: priMenuTitle, menu: priMenu } = primary || {};
  const { title: secMenuTitle, menu: secMenu } = secondary || {};
  const [showSubmenu, setShowSubmenu] = useState<boolean>(false);
  const [showDefaultImage, setShowDefaultImage] = useState<boolean>(true);
  const { isMobile } = useGlobalState();
  const location = useLocation();

  // Ref to hold timeout IDs
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);

  // Hide submenu on resize
  useEffect(() => {
    setShowSubmenu(false);
    document.body.classList.remove(styles.subMenuOpen);
  }, [isMobile]);

  // Hide the submenu when showMenu becomes false
  useEffect(() => {
    if (!showMenu) {
      setTimeout(() => {
        setShowSubmenu(false);
        document.body.classList.remove(styles.subMenuOpen);
      }, 600);
    }
  }, [showMenu]);

  if(!node) {
    return false;
  }

  // desktop func
  const handleMouseEnter = () => {
    // If the page isn’t fully loaded, do nothing.
    if (!pageLoaded) return;

    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current); // Clear any existing timeouts to avoid multiple triggers
    }

    hoverTimeout.current = setTimeout(() => {
      setShowSubmenu(true);
      document.body.classList.add(styles.subMenuOpen);
    }, 100); // Adjust delay time as needed
  }

  const handleMouseLeave = () => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current); // Clear hover timeout on leave
    }

    hoverTimeout.current = setTimeout(() => {
      setShowSubmenu(false);
      document.body.classList.remove(styles.subMenuOpen);
    }, 100); // Adjust delay time as needed
  }

  const handleLinkMouseEnter = () => {
    setShowDefaultImage(false);
  }

  const handleLinkMouseLeave = () => {
    setShowDefaultImage(true);
  }

  // mobile func
  const handleShowSubmenu = (e) => {
    e.preventDefault();
    setShowSubmenu(true);
    document.body.classList.add(styles.subMenuOpen);
  };

  const handleHideSubmenu = (e) => {
    let delay = 600;

    if(getCleanPathname(e?.target?.getAttribute("href")) === location.pathname || e?.target?.tagName === "BUTTON" || !e) {
      delay = 0;
    }

    setTimeout(() => {
      setShowSubmenu(false);
      document.body.classList.remove(styles.subMenuOpen, 'header-no-transition');
    }, delay);
  }

  const renderSubmenu = () => (priMenu?.length > 0 || secMenu?.length > 0) && (
    <div className={styles.submenu}>
      <div className={styles.header}>
        <button className={cn("body-small", styles.back)} onClick={handleHideSubmenu}>
          <Icon symbol="arrow-left" />Main Menu
        </button>
      </div>
      <div className={styles.main}>
        <div className={styles.priMenu}>
          {priMenuTitle && (
            <div className={cn("body-small", styles.menuTitle)}>{priMenuTitle}</div>
          )}

          {priMenu?.length > 0 && (
            <ul>
              {priMenu.map(node => (
                <li key={node._key}>
                  <LinkElem 
                    node={node} 
                    noStyle={true} 
                    onClick={isMobile ? onClick : handleHideSubmenu} 
                    onMouseEnter={isMobile ? undefined : handleLinkMouseEnter} 
                    onMouseLeave={isMobile ? undefined : handleLinkMouseLeave}
                  />
                  {node.image?.asset && (
                    <div className={styles.image}>
                      <ImageG node={node.image} />
                    </div>
                  )}
                </li>
              ))}

              {additionalLink?.url && (
                <li className={styles.additionalLink}>
                  <LinkElem node={additionalLink} noStyle={true} onClick={isMobile ? onClick : handleHideSubmenu} icon="chevron-right" />
                </li>
              )}
            </ul>
          )}
        </div>
        
        <div className={styles.secMenu}>
          {secMenuTitle && (
            <div className={cn("body-small", styles.menuTitle)}>{secMenuTitle}</div>
          )}

          {secMenu?.length > 0 && (
            <ul>
              {secMenu.map(node => (
                <li key={node._key}>
                  <LinkElem 
                    node={node} 
                    noStyle={true} 
                    onClick={isMobile ? onClick : handleHideSubmenu} 
                    onMouseEnter={isMobile ? undefined : handleLinkMouseEnter} 
                    onMouseLeave={isMobile ? undefined : handleLinkMouseLeave}
                  />
                  {node.image?.asset && (
                    <div className={styles.image}>
                      <ImageG node={node.image} />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {defaultImage?.asset && (
          <div className={cn(styles.image, !showDefaultImage && styles.hide)}>
            <ImageG node={defaultImage} />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <li 
      className={cn(styles.root, (priMenu?.length > 0 || secMenu?.length > 0) && styles.hasChildren, showSubmenu && styles.open)}
      onMouseEnter={(!isMobile && (priMenu?.length > 0 || secMenu?.length > 0)) ? handleMouseEnter : undefined} 
      onMouseLeave={(!isMobile && (priMenu?.length > 0 || secMenu?.length > 0)) ? handleMouseLeave : undefined}
    >
      <LinkElem node={node} noStyle={true} onClick={isMobile ? ((priMenu?.length > 0 || secMenu?.length > 0) ? handleShowSubmenu : onClick) : handleHideSubmenu} icon={((priMenu?.length > 0 || secMenu?.length > 0) && isMobile) ? "chevron-right" : undefined} />
      {renderSubmenu()}
    </li>
  );
}

export default MenuItem;
